/**
 * The Risk Rating table's column headings and rating bands.
 *
 * Deliberately a mirror of RISK_RATING_LABELS / RISK_RATING_ROWS /
 * riskRatingConfig in backend/utils/report-helpers.js, the same arrangement
 * nextStepsDefaults.js already carries: the settings panel shows these literals
 * as the value of every control, so if one side changes and the other does not,
 * the editor starts lying about what the report will say.
 *
 * It did lie, until these were shared. The preview used to hardcode its own
 * wording -- 'Immediate threat requiring urgent action' where the document said
 * 'A vulnerability was discovered that has been rated as critical...' -- and its
 * own CVSS strings, so the table an author approved on screen was never the one
 * that printed.
 *
 * ABSENCE MEANS THE STANDARD SCALE, not an empty table: a template saved before
 * these were editable carries no ratings of its own, and must keep printing
 * exactly what it printed before.
 */

export const RISK_RATING_LABELS = {
  index: '#', rating: 'Risk Rating', score: 'CVSSv3.1', description: 'Description',
};

export const RISK_RATING_ROWS = [
  { severity: 'Critical', score: '9.0 - 10', description: 'A vulnerability was discovered that has been rated as critical. This requires resolution as quickly as possible.' },
  { severity: 'High', score: '7.0 \u2013 8.9', description: 'A vulnerability was discovered that has been rated as high. This requires resolution in a short term.' },
  { severity: 'Medium', score: '4.0 \u2013 6.9', description: 'A vulnerability was discovered that has been rated as medium. This should be resolved throughout the ongoing maintenance process.' },
  { severity: 'Low', score: '0.1 \u2013 3.9', description: 'A vulnerability was discovered that has been rated as low. This should be addressed as part of routine maintenance tasks.' },
  { severity: 'Info', score: '0', description: 'A discovery was made that is reported for information. This should be addressed in order to meet leading practice.' },
];

/** Mirrors SEVERITY_COLORS in backend/utils/report-helpers.js. */
export const RISK_RATING_SEVERITY_COLORS = {
  Critical: '#7d1d1d', High: '#dc2626', Medium: '#f97316', Low: '#eab308', Info: '#3b82f6',
};

/** The fill for a band whose name is not one of the five the app scores by. */
export const RISK_RATING_FALLBACK_COLOR = '#6b7280';

export function riskRatingHex(value) {
  if (typeof value !== 'string') return null;
  const cleaned = value.replace('#', '').trim();
  if (/^[0-9a-fA-F]{6}$/.test(cleaned)) return '#' + cleaned.toLowerCase();
  if (/^[0-9a-fA-F]{3}$/.test(cleaned)) {
    const [r, g, b] = cleaned.toLowerCase();
    return '#' + r + r + g + g + b + b;
  }
  return null;
}

/** The colour a band will print in. Mirrors the resolver's own fallback chain. */
export function riskRatingColor(row) {
  return riskRatingHex(row && row.color)
    || RISK_RATING_SEVERITY_COLORS[row && row.severity]
    || RISK_RATING_FALLBACK_COLOR;
}

/** A fresh set of bands, for a section that has not been edited yet. */
export function riskRatingDefaultRows() {
  return RISK_RATING_ROWS.map(r => ({ ...r }));
}

/**
 * What the section will print: the author's bands where the template sets any,
 * the standard scale where it does not. Mirrors riskRatingConfig.
 */
export function riskRatingConfig(config) {
  const cfg = config || {};
  const labels = { ...RISK_RATING_LABELS };
  const given = (cfg.columnLabels && typeof cfg.columnLabels === 'object') ? cfg.columnLabels : {};
  Object.keys(labels).forEach(key => {
    if (typeof given[key] === 'string') labels[key] = given[key];
  });

  const source = Array.isArray(cfg.ratings) ? cfg.ratings : RISK_RATING_ROWS;
  const rows = source
    .filter(r => r && typeof r === 'object')
    .map(r => ({
      severity: r.severity == null ? '' : String(r.severity),
      score: r.score == null ? '' : String(r.score),
      description: r.description == null ? '' : String(r.description),
      color: riskRatingColor(r),
    }));

  return { labels, rows };
}
